/**
 * Role API Functions
 *
 * API functions for company role CRUD operations
 */

import type {
  CompanyRole,
  CreateRoleDto,
  UpdateRoleDto,
} from '@lognai/plugin-admin';
import type { AccessObject } from '@lognai/plugin-sdk';
import { apiFetch } from './client';

const BASE_URL = '/api/companies';

/**
 * The permissions this company may put on its own roles.
 *
 * Not `/api/admin/access-objects`: that is the platform's registry of every
 * access object, gated on the PLATFORM-scoped `admin:access_object:read`,
 * which no company role holds. This returns the COMPANY-scoped subset the
 * company's enabled plugins entitle it to, under `company:role:read`.
 */
export const listGrantableAccessObjects = async (
  companyId: string
): Promise<AccessObject[]> =>
  apiFetch<AccessObject[]>(`${BASE_URL}/${companyId}/roles/access-objects`);

/**
 * Get list of roles for a company
 */
export const listRoles = async (companyId: string): Promise<CompanyRole[]> => {
  // The endpoint returns { roles: CompanyRole[] }; unwrap to the declared array
  // shape callers expect (CompanyRolesSection, MemberDetailPage, InvitationCreatePage).
  const res = await apiFetch<{ roles: CompanyRole[] }>(`${BASE_URL}/${companyId}/roles`);
  return res?.roles ?? [];
};

/**
 * Get a single role by ID
 */
export const getRole = async (
  companyId: string,
  roleId: string
): Promise<CompanyRole> => {
  return apiFetch<CompanyRole>(`${BASE_URL}/${companyId}/roles/${roleId}`);
};

/**
 * Create a new role
 */
export const createRole = async (
  companyId: string,
  dto: CreateRoleDto
): Promise<CompanyRole> => {
  return apiFetch<CompanyRole>(`${BASE_URL}/${companyId}/roles`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
};

/**
 * Update an existing role
 */
export const updateRole = async (
  companyId: string,
  roleId: string,
  dto: UpdateRoleDto
): Promise<CompanyRole> => {
  return apiFetch<CompanyRole>(`${BASE_URL}/${companyId}/roles/${roleId}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
};

/**
 * Delete a role
 */
export const deleteRole = async (
  companyId: string,
  roleId: string
): Promise<void> => {
  return apiFetch<void>(`${BASE_URL}/${companyId}/roles/${roleId}`, {
    method: 'DELETE',
  });
};

/**
 * Copy a role
 */
export const copyRole = async (
  companyId: string,
  roleId: string,
  data: { code: string; name: string; description?: string }
): Promise<CompanyRole> => {
  return apiFetch<CompanyRole>(`${BASE_URL}/${companyId}/roles/${roleId}/copy`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};
